import { api } from "./client";
import type { BillOut, FiscalResult } from "./bills";
import { closeBudgetFor } from "./timeouts";

// Депозитите (BE `app/features/deposits/`) — camelCase, парите като НИЗ.
// Вземането е „Депозит" → „Нов депозит" в Маркиране и издава касов бон (Бон 1,
// ред „ЧАСТИЧНИ ПЛАЩАНИЯ"); „Използвай депозит" го закача към отворената
// сметка (`PUT /bills/{id}/deposit`), а `POST /bills/{id}/close` го приспада
// (Бон 2). Договорът е ред 53 в docs/cross-repo-contract.md.

/** Правото за плащане с депозит (org seed; BE `DEPOSIT_PAYMENT_PERMISSION`). */
export const DEPOSIT_PAYMENT_PERMISSION = "payment_deposit";
/** Правото за вземане на депозит (org seed; BE `DEPOSIT_LOAD_PERMISSION`). */
export const DEPOSIT_LOAD_PERMISSION = "load_deposit";
/** Правото за справката „Депозити" (org seed; BE `PERMISSION_DEPOSITS`). */
export const DEPOSITS_REPORT_PERMISSION = "view_deposits_report";
/** „Анулирай" депозит — същото право като „Възстанови" (BE `DEPOSIT_CANCEL_PERMISSION`). */
export const DEPOSIT_CANCEL_PERMISSION = "restore_last_bill";

/** Закачен — към отворена сметка (приспада се при закриването ѝ); използван —
 *  платена е сметка с него; изтекъл — „За дата" е минала; анулиран — сгрешен
 *  или върнат („Анулирай"), никога в `/deposits/available`. */
export type DepositStatus = "active" | "attached" | "used" | "expired" | "cancelled";

/**
 * Касовият бон на депозита (Бон 1), както го е записало BE:
 *  • `printed` — бонът е излязъл; `not_required` — методът е без
 *    фискализиране за принтера на взелия (бон по закон няма);
 *  • `pending` — чака принтера; `unknown` — изходът не е известен (повторът
 *    е със СЪЩИЯ ключ и не печата втори бон);
 *  • `failed` — принтерът е отказал, картата не е теглена;
 *  • `card_charged` — картата е теглена, бонът не е — НЕ теглете повторно.
 * `null` — заварен депозит отпреди касовия бон (0.0.361).
 */
export type DepositFiscalStatus =
  | "pending"
  | "printed"
  | "failed"
  | "unknown"
  | "card_charged"
  | "not_required";

export interface Deposit {
  id: number;
  name: string;
  amount: string;
  description: string | null;
  paymentMethodId: number;
  paymentMethodNameBg: string | null;
  paymentMethodNameEn: string | null;
  /** „За дата" (YYYY-MM-DD) — последният ден, в който важи; null — безсрочен. */
  validUntil: string | null;
  status: DepositStatus;
  createdAt: string;
  createdByUserId: number;
  createdByName: string | null;
  invoiceId: number | null;
  invoiceNumber: string | null;
  /** Влязъл ли е в персонален отчет (вземането е отчетено). */
  reported: boolean;
  /** Сметката: платената с него (`used`) или отворената, към която е
   *  закачен (`attached` — без `usedAt`/`usedBy*`). */
  usedBillId: number | null;
  usedBillName: string | null;
  usedAt: string | null;
  usedByUserId: number | null;
  usedByName: string | null;
  /** Платеното с депозита — min(депозит, сметка). */
  usedAmount: string;
  /** Неползваната част на използван депозит или целият изтекъл. */
  burnedAmount: string;
  /** Касовият бон на депозита — виж `DepositFiscalStatus`. */
  fiscalStatus: DepositFiscalStatus | null;
  receiptNumber: string | null;
  receiptDatetime: string | null;
  /** Може ли да плати сметка и да се фактурира: `printed` или `not_required`
   *  (BE го смята; `GET /deposits/available` връща и неготовите). */
  fiscalReady: boolean;
  /** ДДС ставката на аванса (BE `receipt_deposit.deposit_vat_rate`): 20, а при
   *  фирма без ДДС регистрация 0 — редът на фактурата за депозита. Липсва при
   *  по-старо BE (тогава 20 — `depositInvoice.ts`). */
  vatRate?: number;
  /** Анулирането (`POST /deposits/{id}/cancel`); null — не е анулиран.
   *  Липсват при по-старо BE. */
  cancelledAt?: string | null;
  cancelledByUserId?: number | null;
  cancelledByName?: string | null;
  cancelReason?: string | null;
}

export interface DepositCreateIn {
  name: string;
  amount: string;
  description: string | null;
  paymentMethodId: number;
  validUntil: string | null;
  /** UUID на модала — повторение със същия ключ не записва втори депозит. */
  requestId: string;
}

/**
 * Отговорът на `POST /deposits` и на `POST /deposits/{id}/receipt` —
 * записаният депозит и суровият изход на бона като при `/close`
 * (`fiscal_result`; null — бон не е искан: `not_required`). HTTP 201
 * (вземането) / 200 („Издай бон") и при неиздаден бон — изходът е в
 * `deposit.fiscalStatus`. Изключението е 402: терминалът доказа, че картата
 * НЕ е теглена — пари не са взети и BE е изтрил депозита (повторът със
 * същия `requestId` е ново вземане). Фактурата за депозит е
 * отделно — `POST /invoices` с `deposit_id`, без нов бон
 * (api/invoices.ts::InvoiceCreatePayload).
 */
export interface DepositReceiptOutcome {
  deposit: Deposit;
  fiscal_result: FiscalResult | null;
}

/** Бюджетът на чакането — като при закриване (`api/timeouts.ts`): карта 240 s,
 *  иначе 30 s; чете се от `paymentMethodId` в тялото. */
export const createDeposit = async (
  data: DepositCreateIn,
): Promise<DepositReceiptOutcome> => {
  const response = await api.post<DepositReceiptOutcome>("/deposits", data);
  return response.data;
};

/**
 * „Издай бон" за депозит без бон (заварен, неуспял, неясен, теглена карта).
 * Тялото е празно — BE ползва метода и (при неясен изход) СЪЩИЯ ключ на бона,
 * затова бюджетът на чакането идва от метода на депозита: без него —
 * картовият (по-дългият, правилото при съмнение в `api/timeouts.ts`).
 */
export const issueDepositReceipt = async (
  depositId: number,
  paymentMethodId?: number,
): Promise<DepositReceiptOutcome> => {
  const response = await api.post<DepositReceiptOutcome>(
    `/deposits/${depositId}/receipt`,
    {},
    paymentMethodId === undefined
      ? undefined
      : { timeout: closeBudgetFor({ paymentMethodId }) },
  );
  return response.data;
};

/**
 * Отговорът на „Анулирай": анулираният депозит; `receiptInDevice` — Бон 1 е
 * отпечатан и ОСТАВА в апарата (автоматично сторно няма — парите се връщат
 * със сторно на него от касата); `cancelledInvoiceNumber` — фактурата за
 * депозита, анулирана заедно с него, и `cancelledInvoiceId` — нейното id (за
 * протокола за анулиране; липсва при по-старо BE); `notice` — същото с думи
 * (български).
 */
export interface DepositCancelOutcome {
  deposit: Deposit;
  receiptInDevice: boolean;
  cancelledInvoiceNumber: string | null;
  cancelledInvoiceId?: number | null;
  notice: string;
}

/**
 * „Анулирай" — само свободен депозит (не закачен, не използван, Бон 1 с
 * потвърден изход), с причина. 409 — защо не (текстът е на BE); повторът
 * на вече анулиран връща записаното. Право `restore_last_bill`, само облак.
 */
export const cancelDeposit = async (
  depositId: number,
  reason: string,
): Promise<DepositCancelOutcome> => {
  const response = await api.post<DepositCancelOutcome>(`/deposits/${depositId}/cancel`, { reason });
  return response.data;
};

/** Депозитите за „Използвай депозит": активните и закачените към отворена
 *  сметка (`attached` — към друга маса е неактивен), и тези без бон —
 *  неактивни, с `fiscalReady=false`. */
export const getAvailableDeposits = async (): Promise<Deposit[]> => {
  const response = await api.get<{ items: Deposit[] }>("/deposits/available");
  return response.data.items;
};

/**
 * „Използвай депозит" — закача депозита към ОТВОРЕНАТА сметка (сменя
 * закачения). BE записва само `bills.deposit_id`; колко покрива се смята при
 * закриването. Отговорът е документът на сметката. Право `payment_deposit`,
 * само облак (423 в локален режим).
 */
export const attachBillDeposit = async (billId: number, depositId: number): Promise<BillOut> => {
  const response = await api.put<BillOut>(`/bills/${billId}/deposit`, { depositId });
  return response.data;
};

/** „Махни депозита" — сметката е без депозит, той пак е активен. */
export const detachBillDeposit = async (billId: number): Promise<BillOut> => {
  const response = await api.delete<BillOut>(`/bills/${billId}/deposit`);
  return response.data;
};

/** По коя дата е периодът на справката. */
export type DepositsReportBasis = "created" | "used" | "valid_until";

export interface DepositsReportFilters {
  start_date?: string;
  end_date?: string;
  basis: DepositsReportBasis;
}

export interface DepositsReportResponse {
  items: Deposit[];
  /** Активните депозити В МОМЕНТА — независимо от периода. */
  outstandingCount: number;
  outstandingAmount: string;
}

export const getDepositsReport = async (
  filters: DepositsReportFilters,
): Promise<DepositsReportResponse> => {
  const response = await api.get<DepositsReportResponse>(
    "/reports/analytics/deposits",
    { params: filters },
  );
  return response.data;
};
